import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, SlidersHorizontal, RotateCcw } from 'lucide-react';

import api from '../../../services/api';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../../components/ui/GenericSelect';
import Button from '../../../components/ui/Button';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { GOAL_TYPES } from '../../../components/performance/goalTypes';
import {
  DIRECTIONS, EVIDENCE, GOAL_STATUSES, RISKS, activeGoalFilterKeys, clearGoalFilter,
} from '../config';

// E04 hedef süzgeçleri (analiz: F0 + hedef sahibi, tür, durum, ölçüm yönü, risk nedeni, kanıt güncelliği ve son tarih).
// Şeritte tür / risk / kanıt; diğerleri "Hedef Süzgeçleri" çekmecesinde. Etkin süzgeçler silinebilir etiketlerdir;
// grafik ve kart tıklamalarının eklediği koşullar (bant, kurumsal hedef, bağ, geciken) da burada görünür ve kaldırılır.
const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  width: ${({ $w }) => $w || '220px'};
  max-width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const ChipRemove = styled(Button)`
  min-height: 0;
  padding: 0;
  line-height: 0;
`;

const rowsOf = (res) => { const r = res?.data?.data; return Array.isArray(r) ? r : (r?.data || []); };

const employeeLookup = {
  onSearch: async (q) => {
    const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    return rowsOf(res).map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() }));
  },
  onGetById: async (id) => {
    try {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res?.data?.data || {};
      return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() };
    } catch {
      return { id, name: '' };
    }
  },
};

const staticLookup = (values, labelOf) => ({
  minChars: 0,
  onSearch: async () => values.map((v) => ({ id: v, name: labelOf(v) })),
  onGetById: async (id) => ({ id, name: labelOf(id) }),
});

export default function GoalFilterStrip({ goalFilters, onChange, onReset }) {
  const { t } = useTranslation();
  const [drawerInitial, setDrawerInitial] = useState(null);

  const typeLabel = (v) => t(`performance.goal.goalType.${v}`, { defaultValue: v });
  const riskLabel = (v) => t(`performance.goalAnalytics.risk.${v}`);
  const statusLabel = (v) => t(`performance.goal.status.${v}`, { defaultValue: v });
  const types = useMemo(() => staticLookup(GOAL_TYPES, typeLabel), [t]); // eslint-disable-line react-hooks/exhaustive-deps
  const risks = useMemo(() => staticLookup(RISKS, riskLabel), [t]); // eslint-disable-line react-hooks/exhaustive-deps
  const statuses = useMemo(() => staticLookup(GOAL_STATUSES, statusLabel), [t]); // eslint-disable-line react-hooks/exhaustive-deps
  const evidenceOptions = EVIDENCE.map((v) => ({ id: v, name: t(`performance.goalAnalytics.evidence.${v}`) }));

  const openDrawer = () => setDrawerInitial({
    statuses: goalFilters.statuses,
    direction: goalFilters.direction || '',
    dueFrom: goalFilters.dueFrom,
    dueTo: goalFilters.dueTo,
    ownerEmployeeId: goalFilters.ownerEmployeeId,
  });

  const schema = [
    { name: 'statuses', label: t('performance.goalAnalytics.filter.status'), type: 'asyncSelect', isMulti: true, grid: 12, ...statuses },
    {
      name: 'direction', label: t('performance.goalAnalytics.filter.direction'), type: 'select', grid: 12,
      options: [{ id: '', name: t('performance.goalAnalytics.filter.any') },
        ...DIRECTIONS.map((d) => ({ id: d, name: t(`performance.goal.kpiDirection.${d}`) }))],
      idKey: 'id', labelKey: 'name',
    },
    { name: 'dueFrom', label: t('performance.goalAnalytics.filter.dueFrom'), type: 'date', grid: 6 },
    { name: 'dueTo', label: t('performance.goalAnalytics.filter.dueTo'), type: 'date', grid: 6 },
    { name: 'ownerEmployeeId', label: t('performance.goalAnalytics.filter.owner'), type: 'asyncSelect', grid: 12, ...employeeLookup },
  ];

  const chipLabel = (key) => {
    const v = goalFilters[key];
    switch (key) {
      case 'types': return `${t('performance.goalAnalytics.filter.type')}: ${v.map(typeLabel).join(', ')}`;
      case 'risks': return `${t('performance.goalAnalytics.filter.risk')}: ${v.map(riskLabel).join(', ')}`;
      case 'statuses': return `${t('performance.goalAnalytics.filter.status')}: ${v.map(statusLabel).join(', ')}`;
      case 'evidence': return t(`performance.goalAnalytics.evidence.${v}`);
      case 'direction': return t(`performance.goal.kpiDirection.${v}`);
      case 'dueFrom': return `${t('performance.goalAnalytics.filter.dueFrom')}: ${v}`;
      case 'dueTo': return `${t('performance.goalAnalytics.filter.dueTo')}: ${v}`;
      case 'ownerEmployeeId': return t('performance.goalAnalytics.filter.owner');
      case 'band': return `${t('performance.goalAnalytics.filter.band')}: ${t(`performance.goalAnalytics.band.${v}`)}`;
      case 'alignedOrgGoalId': return t('performance.goalAnalytics.filter.alignedOrgGoal');
      case 'linked': return v ? t('performance.goalAnalytics.filter.linked') : t('performance.goalAnalytics.filter.unlinked');
      case 'overdue': return t('performance.goalAnalytics.filter.overdue');
      default: return key;
    }
  };

  const active = activeGoalFilterKeys(goalFilters);

  return (
    <>
      <Bar data-role="goal-filter-strip">
        <Field>
          <AsyncGenericSelect
            label={t('performance.goalAnalytics.filter.type')}
            value={goalFilters.types}
            onChange={(v) => onChange({ ...goalFilters, types: v || [] })}
            isMulti
            compact
            {...types}
          />
        </Field>
        <Field>
          <AsyncGenericSelect
            label={t('performance.goalAnalytics.filter.risk')}
            value={goalFilters.risks}
            onChange={(v) => onChange({ ...goalFilters, risks: v || [] })}
            isMulti
            compact
            {...risks}
          />
        </Field>
        <Field $w="200px">
          <GenericSelect
            label={t('performance.goalAnalytics.filter.evidence')}
            value={goalFilters.evidence}
            onChange={(v) => onChange({ ...goalFilters, evidence: v || null })}
            options={evidenceOptions}
            idKey="id"
            labelKey="name"
          />
        </Field>
        <Button variant="outline" onClick={openDrawer} data-action="goal-filters">
          <SlidersHorizontal size={16} />
          {' '}
          {t('performance.goalAnalytics.filter.more')}
        </Button>
      </Bar>
      {active.length > 0 ? (
        <Chips>
          {active.map((key) => (
            <Tag key={key} data-chip={key}>
              {chipLabel(key)}
              <ChipRemove variant="ghost" aria-label={t('performance.goalAnalytics.filter.remove')} onClick={() => onChange(clearGoalFilter(goalFilters, key))}>
                <X size={12} />
              </ChipRemove>
            </Tag>
          ))}
          <Button variant="ghost" onClick={onReset} data-action="goal-filters-reset">
            <RotateCcw size={14} />
            {' '}
            {t('performance.goalAnalytics.filter.reset')}
          </Button>
        </Chips>
      ) : null}
      <GenericFormDrawer
        open={drawerInitial !== null}
        onClose={() => setDrawerInitial(null)}
        title={t('performance.goalAnalytics.filter.more')}
        schema={schema}
        initialValues={drawerInitial || undefined}
        submitLabel={t('performance.goalAnalytics.filter.apply')}
        onSave={async (values) => {
          onChange({
            ...goalFilters,
            statuses: values.statuses || [],
            direction: values.direction || null,
            dueFrom: values.dueFrom || null,
            dueTo: values.dueTo || null,
            ownerEmployeeId: values.ownerEmployeeId || null,
          });
          return true;
        }}
      />
    </>
  );
}
